/**
 * Phase 16 — Shared homepage sections extracted from the original Home.jsx.
 * Post-Phase 19, the promoted `/` homepage (formerly `/home-v2`) composes from
 * this file. Left here as the canonical location for the below-hero sections.
 *
 * Each section renders its own <section> tag and expects `reveal` to be handled
 * by the parent (which owns the ScrollReveal ref).
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Rocket, Wrench } from "lucide-react";
import UpcomingEvents from "../UpcomingEvents";
import CourseCard from "../CourseCard";
import AudiencePill from "../AudiencePill";

// ── Data (constants moved off Home.jsx so both routes share one source) ────
export const HOW_IT_WORKS = [
  { title: "Pick your track", body: "Choose the department that matches your role." },
  { title: "Learn by doing", body: "Bite-size lessons with real dealership assets." },
  { title: "Ship AI tools", body: "Deploy AI copilots that measurably move numbers." },
  { title: "Earn recognition", body: "Certificates and rank ladder tied to real work." },
];

export const AUDIENCE_TINT = {
  automotive:  { label: "Automotive",   pill: "bg-fc-red/10 text-fc-red border-fc-red/25",           avatar: "bg-fc-red text-white" },
  business:    { label: "Business",     pill: "bg-emerald-50 text-emerald-700 border-emerald-200",   avatar: "bg-emerald-500 text-white" },
  entrepreneur:{ label: "Entrepreneur", pill: "bg-violet-50 text-violet-700 border-violet-200",       avatar: "bg-violet-500 text-white" },
};

export const TESTIMONIALS = [
  { id: "t1", name: "Marcus T.", role: "GM, Multi-rooftop dealer group", location: "", avatarFallback: "MT",
    audience: "automotive", metric: "62% faster follow-up",
    quote: "Cut our internet lead follow-up time by 62% in three weeks. The AI sales co-pilot framework alone paid for the membership ten times over." },
  { id: "t2", name: "Priya S.", role: "Head of Operations", location: "Austin, TX", avatarFallback: "PS",
    audience: "business", metric: "1 workflow > 3-person team",
    quote: "Replaced an offshore team for our customer triage with a Claude-powered workflow we built in week two." },
  { id: "t3", name: "Ravi P.", role: "Founder, SaaS", location: "Toronto, ON", avatarFallback: "RP",
    audience: "entrepreneur", metric: "MVP → paying users in 60 days",
    quote: "Vibecoding 101 was the unlock. I launched my MVP in a weekend without hiring a single engineer. Two months in, paying users." },
];

export const AUDIENCE_CARDS = [
  { slug: "entrepreneur", label: "Entrepreneur",  emoji: "🚀", Icon: Rocket,
    blurb: "Ship AI products fast. From MVP to paying customers.",
    href: "/entrepreneur", cta: "Explore track",
    accentGradient: "from-violet-500/10 to-transparent" },
  { slug: "business", label: "Business", emoji: "💼", Icon: Briefcase,
    blurb: "Operator playbooks for teams. Ops, Sales, Marketing, Service.",
    href: "/business", cta: "Explore track",
    accentGradient: "from-emerald-500/10 to-transparent" },
  { slug: "automotive", label: "Automotive", emoji: "🔧", Icon: Wrench,
    blurb: "Dealership-native AI. Ford, GM, Toyota-tested workflows.",
    href: "/automotive", cta: "Explore track",
    accentGradient: "from-fc-red/10 to-transparent" },
];

// ── Sections (each one is self-contained) ─────────────────────────────────
export function UpcomingEventsSection() {
  return (
    <section className="bg-fc-navy py-20 border-b border-white/5 reveal" data-testid="upcoming-events-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="fc-overline mb-3 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
              UPCOMING LIVE EVENTS · COMPLIMENTARY
            </div>
            <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              Live operator sessions. <span className="text-amber-300">Reserve your seat.</span>
            </h2>
          </div>
          <Link to="/register" className="btn-fc-outline shrink-0" data-testid="upcoming-events-cta">
            Get complimentary access <ArrowRight size={14} />
          </Link>
        </div>
        <UpcomingEvents limit={3} authed={false} variant="public" />
      </div>
    </section>
  );
}

export function AudienceCardsSection() {
  return (
    <section className="bg-[#060A24] py-24 border-y border-white/10" data-testid="audience-cards-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-12 reveal">
          <div className="fc-overline mb-3">PICK YOUR PATH</div>
          <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
            Three academies. <span className="text-fc-red">One mission.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 reveal">
          {AUDIENCE_CARDS.map((a) => {
            const Icon = a.Icon;
            return (
              <Link
                key={a.slug} to={a.href}
                className="relative group bg-fc-navy border border-white/10 hover:border-fc-red p-8 lg:p-10 transition-all hover:-translate-y-1 overflow-hidden"
                data-testid={`audience-card-${a.slug}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${a.accentGradient} opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`} />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-fc-red/15 flex items-center justify-center">
                      <Icon className="text-fc-red" size={22} />
                    </div>
                    <span className="text-2xl" aria-hidden>{a.emoji}</span>
                  </div>
                  <h3 className="text-2xl font-black tracking-tight mb-3">{a.label}</h3>
                  <p className="text-white/65 text-sm leading-relaxed mb-6">{a.blurb}</p>
                  <span className="inline-flex items-center gap-2 text-fc-red text-xs font-bold tracking-[0.18em] uppercase border-b border-fc-red/40 pb-1 group-hover:gap-3 transition-all" data-testid={`audience-card-${a.slug}-cta`}>
                    {a.cta} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  return (
    <section className="bg-fc-navy py-24" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-12 reveal">
          <div className="fc-overline mb-3">HOW IT WORKS</div>
          <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
            From watching to <span className="text-fc-red">building</span> in four steps.
          </h2>
        </div>
        <div className="grid md:grid-cols-4 gap-6 reveal">
          {HOW_IT_WORKS.map((s, i) => (
            <div key={i} className="bg-[#060A24] border border-white/5 p-8" data-testid={`how-step-${i + 1}`}>
              <div className="w-10 h-10 rounded-full bg-fc-red flex items-center justify-center font-black text-white mb-5">{i + 1}</div>
              <h3 className="font-black text-base uppercase tracking-wide mb-2">{s.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FeaturedCoursesSection({ featured }) {
  // Phase 16.2 — defensive filter. Even if a caller forgets to hit
  // /api/courses with include_coming_soon=false, coming-soon and unpublished
  // rows must never surface on the featured rail.
  const safe = (featured || []).filter(
    (c) => c && c.is_published !== false && !c.is_coming_soon,
  );
  return (
    <section className="bg-[#060A24] py-24" data-testid="featured-courses-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12 reveal">
          <div>
            <div className="fc-overline mb-3">CROSS-AUDIENCE FAVORITES</div>
            <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl">
              Start <span className="text-fc-red">anywhere.</span>
            </h2>
          </div>
          <Link to="/courses" className="text-sm font-bold tracking-wider text-white/80 hover:text-fc-red flex items-center gap-1 self-start sm:self-auto">
            VIEW ALL <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
          {safe.map((c) => (
            <div key={c.id} className="relative" data-testid={`featured-course-${c.slug}`}>
              <div className="absolute top-3 left-3 z-10">
                <AudiencePill audiences={c.audiences} testid={`audience-pill-${c.slug}`} />
              </div>
              <CourseCard course={c} featured />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  return (
    <section className="bg-fc-navy py-24" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-12 reveal">
          <div className="fc-overline mb-3">EARLY ACCESS FEEDBACK</div>
          <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl max-w-3xl">Operators don&apos;t lie.</h2>
          <p className="text-white/60 text-sm sm:text-base mt-4 max-w-2xl leading-relaxed">
            Real feedback from operators inside Freddy Compute Academy during early access.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 reveal">
          {TESTIMONIALS.map((t) => {
            const tint = AUDIENCE_TINT[t.audience] || AUDIENCE_TINT.business;
            return (
              <article key={t.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 flex flex-col" data-testid={`testimonial-${t.id}`}>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xs font-black ${tint.avatar}`} aria-hidden="true">{t.avatarFallback}</div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 text-sm truncate">{t.name}</p>
                      <p className="text-gray-500 text-[11px] leading-tight truncate">
                        {t.role}{t.location && <span className="text-gray-400"> · {t.location}</span>}
                      </p>
                    </div>
                  </div>
                  <span className={`flex-shrink-0 text-[9px] tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded-full border ${tint.pill}`}>{tint.label}</span>
                </div>
                <p className="text-gray-800 text-sm leading-relaxed mb-5 flex-1">&ldquo;{t.quote}&rdquo;</p>
                {t.metric && (
                  <div>
                    <span className="inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.12em] uppercase font-bold rounded-full bg-amber-100 text-amber-800 border border-amber-200">{t.metric}</span>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PricingTeaserSection({ plans }) {
  return (
    <section className="bg-[#060A24] py-24" data-testid="pricing-teaser-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12 reveal">
          <div className="fc-overline mb-3">SIMPLE. ANNUAL.</div>
          <h2 className="fc-h-section text-3xl sm:text-4xl lg:text-5xl">
            Two plans. One <span className="text-fc-red">AI-first</span> playbook.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 reveal">
          {plans.map((p) => (
            <div key={p.id} className={`p-8 lg:p-10 bg-fc-navy border ${p.is_recommended ? "border-fc-red border-2 relative overflow-hidden" : "border-white/10"}`} data-testid={`pricing-teaser-${p.slug}`}>
              {p.is_recommended && (
                <div className="absolute top-0 right-0 bg-fc-red text-white text-[10px] tracking-[0.2em] font-bold px-3 py-1.5">MOST POPULAR</div>
              )}
              <p className="text-fc-red text-xs tracking-[0.2em] font-bold uppercase mb-3">{p.name}</p>
              <div className="flex items-end gap-2 mb-5">
                <span className="text-5xl font-black tracking-tight">{p.price_display}</span>
                <span className="text-white/50 text-sm pb-2">/year</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-6">{p.description}</p>
              <Link to={`/register?plan=${p.slug}&landing=home`} className={p.is_recommended ? "btn-fc-red w-full justify-center" : "btn-fc-outline w-full justify-center"} data-testid={`pricing-teaser-${p.slug}-cta`}>
                Choose {p.name.split(" ")[0]}
              </Link>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/pricing" className="text-sm font-bold tracking-wider text-white/70 hover:text-fc-red">
            SEE FULL PLAN COMPARISON →
          </Link>
        </div>
      </div>
    </section>
  );
}
